import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { CheckIcon, ChevronDownIcon, SearchIcon } from "./icons";
import { cn, focusRing } from "./utils";

export type SelectOption = {
  value: string;
  label: string;
  /** Quiet text at the end of the row, such as a calling code. */
  detail?: string;
  /** What the closed control shows, when it should be shorter than the label. */
  short?: string;
  /** Extra words the search matches, such as a country code. */
  keywords?: string;
};

export type SelectProps = {
  /** The accessible name. Shown above the control unless `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Adds a search field above the list, for long lists. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Classes for the closed control. */
  className?: string;
  /** Classes for the open list, such as a width wider than the control. */
  menuClassName?: string;
};

/**
 * A dropdown in the app's own style. The closed control is a button, and the open list is an
 * app-styled panel rather than the operating system's menu, which no stylesheet can reach.
 */
export function Select({
  label,
  hideLabel = false,
  value,
  options,
  onChange,
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search",
  className,
  menuClassName,
}: SelectProps) {
  const id = useId();
  const listId = `${id}-list`;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Where the open list sits on screen. It is drawn at the end of the page, so a scrolling sheet
  // around the control cannot clip it.
  const [place, setPlace] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(
    null,
  );
  const typed = useRef({ text: "", at: 0 });

  const selected = options.find((o) => o.value === value);
  // A search matches the start of any word, and options whose label starts with it come first.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, "");
    if (!q) return options;
    // Searching "united s" finds United States: the text has to start where a word starts.
    const text = (o: SelectOption) =>
      ` ${o.label} ${o.detail ?? ""} ${o.keywords ?? ""}`.toLowerCase().replace(/[,()+-]/g, " ");
    const hits = options.filter((o) => text(o).includes(` ${q}`));
    const first = (o: SelectOption) => (o.label.toLowerCase().startsWith(q) ? 0 : 1);
    return hits.sort((a, b) => first(a) - first(b));
  }, [options, query]);

  const close = (refocus = true) => {
    setOpen(false);
    setQuery("");
    if (refocus) trigger.current?.focus();
  };

  const measure = () => {
    const box = trigger.current?.getBoundingClientRect();
    if (!box) return;
    const below = window.innerHeight - box.bottom;
    // Opens upwards when the space below the control is shorter than the list.
    const up = below < 300 && box.top > below;
    setPlace({
      left: box.left,
      width: box.width,
      ...(up ? { bottom: window.innerHeight - box.top + 6 } : { top: box.bottom + 6 }),
    });
  };

  const openList = () => {
    if (disabled) return;
    measure();
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  };

  const choose = (option: SelectOption | undefined) => {
    if (!option) return;
    onChange(option.value);
    close();
  };

  useLayoutEffect(() => {
    if (!open) return;
    (searchable ? search.current : list.current)?.focus();
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!root.current?.contains(target) && !panel.current?.contains(target)) close(false);
    };
    const follow = () => measure();
    document.addEventListener("pointerdown", away);
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    return () => {
      document.removeEventListener("pointerdown", away);
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow, true);
    };
  });

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  const onListKey = (e: KeyboardEvent<HTMLElement>) => {
    const last = shown.length - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(last, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Home" && !searchable) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End" && !searchable) {
      e.preventDefault();
      setActive(last);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(shown[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      close(false);
    } else if (!searchable && e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
      // Typing jumps to the first option that starts with what was typed.
      const now = Date.now();
      const text = (now - typed.current.at < 700 ? typed.current.text : "") + e.key.toLowerCase();
      typed.current = { text, at: now };
      const hit = shown.findIndex((o) => o.label.toLowerCase().startsWith(text));
      if (hit >= 0) setActive(hit);
    }
  };

  const activeId = shown[active] ? `${id}-opt-${active}` : undefined;

  return (
    <div ref={root} className="relative flex min-w-0 flex-col gap-1.5">
      {!hideLabel && (
        <span id={`${id}-label`} className="text-[13px] font-semibold text-ink">
          {label}
        </span>
      )}
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-label={hideLabel ? label : undefined}
        aria-labelledby={hideLabel ? undefined : `${id}-label`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-value={value}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKey}
        className={cn(
          "flex h-11 min-w-0 cursor-pointer items-center justify-between gap-2 rounded-field border border-line bg-surface px-3.5 text-left font-sans text-[15px] text-ink transition-colors hover:bg-linen disabled:cursor-not-allowed disabled:opacity-50",
          open && "border-blue",
          focusRing,
          className,
        )}
      >
        <span className="truncate">{selected ? (selected.short ?? selected.label) : label}</span>
        <ChevronDownIcon
          size={16}
          strokeWidth={2.2}
          className={cn("flex-none text-ink-secondary transition-transform", open && "rotate-180")}
        />
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={panel}
            style={{
              position: "fixed",
              left: place.left,
              top: place.top,
              bottom: place.bottom,
              minWidth: place.width,
            }}
            className={cn(
              "z-[60] flex max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-[16px] border border-line bg-surface font-sans shadow-[0_12px_32px_rgba(22,26,38,0.14)]",
              menuClassName,
            )}
          >
            {searchable && (
              <div className="flex items-center gap-2 border-b border-line px-3">
                <SearchIcon size={16} className="flex-none text-ink-meta" />
                <input
                  ref={search}
                  type="search"
                  aria-label={searchPlaceholder}
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onListKey}
                  className="h-11 min-w-0 flex-1 appearance-none border-0 bg-transparent font-sans text-[15px] text-ink outline-none placeholder:text-ink-meta [&::-webkit-search-cancel-button]:appearance-none"
                />
              </div>
            )}
            <div
              ref={list}
              id={listId}
              role="listbox"
              aria-label={label}
              tabIndex={-1}
              aria-activedescendant={searchable ? undefined : activeId}
              onKeyDown={searchable ? undefined : onListKey}
              className="max-h-[min(320px,45vh)] overflow-y-auto p-1.5 outline-none"
            >
              {shown.length === 0 && (
                <p className="m-0 px-3 py-2.5 text-sm text-ink-meta">Nothing matches.</p>
              )}
              {shown.map((o, i) => (
                <Row
                  key={o.value}
                  id={`${id}-opt-${i}`}
                  index={i}
                  option={o}
                  selected={o.value === value}
                  active={i === active}
                  onHover={() => setActive(i)}
                  onChoose={() => choose(o)}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function Row({
  id,
  index,
  option,
  selected,
  active,
  onHover,
  onChoose,
}: {
  id: string;
  index: number;
  option: SelectOption;
  selected: boolean;
  active: boolean;
  onHover: () => void;
  onChoose: () => void;
}): ReactNode {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox handles the keys for every option
    <div
      id={id}
      role="option"
      aria-selected={selected}
      // Focus stays on the list or the search field, which point at this row with aria-activedescendant.
      tabIndex={-1}
      data-index={index}
      onPointerMove={onHover}
      // Keeps focus in the search field or list while the pointer is down.
      onPointerDown={(e) => e.preventDefault()}
      onClick={onChoose}
      className={cn(
        "flex min-h-10 cursor-pointer items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-[15px] text-ink",
        active && "bg-surface-header",
        selected && "font-semibold",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
      {option.detail && <span className="flex-none text-sm text-ink-secondary">{option.detail}</span>}
      <CheckIcon
        size={16}
        strokeWidth={2.4}
        className={cn("flex-none text-blue", !selected && "invisible")}
      />
    </div>
  );
}
